<template>
  <div class="content">
    <progress-bar :percent="20" class="progress-bar" />

    <div class="center">
      <a-progress
        type="circle"
        :percent="Math.round(progress)"
        stroke-color="#FFE86C"
        trail-color="#005149"
        :width="158"
        class="big-progress"
      >
        <template #format="percent">
          <span
            style="
              color: #005149;
              font-size: 40px;
              font-family: Helvetica Light, sans-serif;
            "
            >{{ percent }}</span
          >
          <span
            style="
              color: #005149;
              font-family: Helvetica Light, sans-serif;
              font-size: 12px;
            "
            >%</span
          >
        </template>
      </a-progress>

      <div class="notice">
        We have noticed this unit could do with a software update.
      </div>

      <div class="note">
        Please wait and ensure the unit stays powered whilst we complete this.
        This may take up to 20 minutes.
      </div>
    </div>

    <back-button />
  </div>
</template>

<script>
import waitFor from "p-wait-for";
import {
  SOFTWARE_UPDATE_INTERVAL_MS,
  SOFTWARE_UPDATE_TIMEOUT_MS,
} from "../util/constants";
import {
  decideSoftwareUpdateAction,
  normalizeSoftwareState,
} from "../util/software-update";

export default {
  name: "SoftwareUpdate",
  data() {
    return {
      progress: 0,
      isConcluded: false,
      isSuccessful: false,
      intendedVersion: "",
      progressIntervalId: null,
      // Skip re-POST while Redis has not yet shown the hop; reset on poll so
      // a later idle tick can POST desired again after a gate hop finishes.
      applyIssued: false,
    };
  },
  mounted() {
    this.startUpdate();
  },
  beforeDestroy() {
    this.clearProgressInterval();
  },
  methods: {
    clearProgressInterval() {
      if (this.progressIntervalId !== null) {
        clearInterval(this.progressIntervalId);
        this.progressIntervalId = null;
      }
    },
    startTimedProgress() {
      const INTERVAL_5_S = 5000;
      this.clearProgressInterval();
      this.progressIntervalId = setInterval(() => {
        if (this.isConcluded) {
          this.clearProgressInterval();
          return;
        }
        // Asymptotic approach toward 99% while waiting on the device.
        const remaining = 99 - this.progress;
        this.progress = Math.min(99, this.progress + remaining * 0.08);
      }, INTERVAL_5_S);
    },
    async fetchSoftwareState() {
      const { data } = await this.$http.get(
        this.$api.getSolshareSoftware(this.$store.state.project.serialNumber)
      );
      return normalizeSoftwareState(data);
    },
    async applySoftwareVersion(version) {
      await this.$http.post(
        this.$api.applySolshareSoftware(this.$store.state.project.serialNumber),
        { version },
        {
          validateStatus: (status) =>
            status === 204 || (status >= 200 && status < 300),
        }
      );
    },
    async tickUpdate() {
      const state = await this.fetchSoftwareState();
      if (!this.intendedVersion) {
        this.intendedVersion = (state.solshareVersion.desired || "").trim();
      }

      const action = decideSoftwareUpdateAction(state, this.intendedVersion);

      if (action.type === "done") {
        this.isConcluded = true;
        this.isSuccessful = true;
        this.progress = 100;
        return true;
      }

      if (action.type === "fail") {
        this.isConcluded = true;
        this.isSuccessful = false;
        return true;
      }

      if (action.type === "poll") {
        this.applyIssued = false;
        return false;
      }

      if (this.applyIssued) {
        return false;
      }

      await this.applySoftwareVersion(action.version);
      this.applyIssued = true;
      return false;
    },
    async pollUpdateStatus() {
      try {
        await waitFor(async () => this.tickUpdate(), {
          interval: SOFTWARE_UPDATE_INTERVAL_MS,
          timeout: SOFTWARE_UPDATE_TIMEOUT_MS,
          // startUpdate already ran one tick; avoid an immediate second POST.
          leadingCheck: false,
        });

        if (this.isConcluded && this.isSuccessful) {
          this.$store.dispatch("resetSoftwareUpdateState");
          await this.$router.push("/software-update-success");
        } else {
          await this.$router.push("/software-update-failed");
        }
      } catch (e) {
        console.error("Error updating software", e);
        await this.$router.push("/software-update-failed");
      }
    },
    async startUpdate() {
      try {
        await this.tickUpdate();
      } catch (e) {
        console.error("Error triggering software update", e);
        await this.$router.push("/software-update-failed");
        return;
      }

      this.startTimedProgress();
      if (!this.isConcluded) {
        await this.pollUpdateStatus();
      } else if (this.isSuccessful) {
        this.$store.dispatch("resetSoftwareUpdateState");
        await this.$router.push("/software-update-success");
      } else {
        await this.$router.push("/software-update-failed");
      }
    },
  },
};
</script>

<style lang="less" scoped>
.content {
  .progress-bar {
    margin-bottom: 41px;
  }

  .big-progress {
    margin-bottom: 40px;
  }

  .notice {
    margin-bottom: 30px;
    font-size: 25px;
    color: #005149;
    font-family: "Helvetica Light", sans-serif;
    line-height: 29px;
  }

  .note {
    margin-bottom: 56px;
    font-size: 17px;
    font-family: "Helvetica Light", sans-serif;
  }

  .center {
    text-align: center;
  }
}
</style>
